import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import {
  Database,
  Search,
  Filter,
  Plus,
  Calendar,
  Building2,
  TrendingUp,
  MoreHorizontal,
  List,
  Grid3x3,
  SortAsc,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  FileText,
  Award,
  Sparkles,
  Upload,
  Users,
  Briefcase,
  Archive,
  BarChart3,
} from 'lucide-react';
import { Container } from '../styles/GlobalStyles';
import { theme } from '../styles/theme';
import Particles from '../components/Particles';
import TenderRow from '../components/TenderRow';
import ViewToggle from '../components/ViewToggle';
import TenderUploadModal from '../components/TenderUploadModal';
import TenderBankAssistant from '../components/TenderBankAssistant';
import { TenderDocumentApi, TenderDocumentData } from '../services/tenderDocumentApi';

// =============================================================================
// MOTION
// =============================================================================

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const viewSwap = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// =============================================================================
// PAGE SHELL — coherent with /profile, /settings, /contact, /knowledge-base
// =============================================================================

const TenderBankContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[8]} 0 ${theme.spacing[16]};
  margin: 30px 30px 0;
  border: 1px solid ${theme.colors.border};
  border-radius: 20px;
  position: relative;
  /* clip (not hidden) so the sticky toolbar can attach to the viewport top
     instead of being trapped here. */
  overflow: clip;

  @media (max-width: ${theme.breakpoints.md}) {
    margin: 20px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    margin: 0;
    border-radius: 0;
    border: none;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing[4]};
  animation: ${fadeUp} 0.45s ease-out both;
`;

// =============================================================================
// PAGE HEADER
// =============================================================================

const PageHeader = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[8]};
  padding-bottom: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.borderLight};

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.spacing[4]};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  min-width: 0;
`;

const Crumb = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.textSecondary};
  margin: ${theme.spacing[1]} 0 0;
  max-width: 580px;
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${theme.spacing[3]};
  flex-shrink: 0;

  @media (max-width: ${theme.breakpoints.md}) {
    align-items: stretch;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`;

type StatusTone = 'success' | 'primary';

const StatusPill = styled.div<{ $tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 8px ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  box-shadow: ${theme.shadows.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  white-space: nowrap;

  & strong {
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
    font-variant-numeric: tabular-nums;
  }

  & .pulse {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    ${({ $tone }) =>
      $tone === 'success'
        ? css`
            background: ${theme.colors.success[500]};
            box-shadow: 0 0 0 4px ${theme.colors.success[500]}24;
          `
        : css`
            background: ${theme.colors.primary[500]};
            box-shadow: 0 0 0 4px ${theme.colors.primary[500]}24;
          `}
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: ${theme.colors.primary[500]};
  color: #fff;
  border: 1px solid ${theme.colors.primary[600]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    background ${theme.transitions.base};
  box-shadow: 0 6px 14px -6px rgba(0, 0, 41, 0.45);
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[700]};
    transform: translateY(-1px);
    box-shadow: 0 10px 18px -6px rgba(0, 0, 41, 0.55);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

// =============================================================================
// STATS GRID
// =============================================================================

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[8]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    border-color ${theme.transitions.base};

  &:hover {
    transform: translateY(-2px);
    border-color: ${theme.colors.primary[200]};
    box-shadow: 0 8px 18px -10px rgba(0, 0, 41, 0.18);
  }
`;

type StatTone = 'primary' | 'success' | 'warning' | 'accent';

const StatIconTile = styled.span<{ $tone: StatTone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid;

  ${({ $tone }) =>
    $tone === 'success'
      ? css`
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[600]};
          border-color: ${theme.colors.success[500]}40;
        `
      : $tone === 'warning'
      ? css`
          background: ${theme.colors.warning[100]};
          color: ${theme.colors.warning[600]};
          border-color: ${theme.colors.warning[500]}40;
        `
      : $tone === 'accent'
      ? css`
          background: ${theme.colors.primary[50]};
          color: ${theme.colors.primary[700]};
          border-color: ${theme.colors.primary[200]};
        `
      : css`
          background: ${theme.colors.primary[50]};
          color: ${theme.colors.primary[500]};
          border-color: ${theme.colors.primary[100]};
        `}
`;

const StatBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatValueWrap = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing[1]};
`;

const StatValue = styled.div`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 30px;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  letter-spacing: -0.025em;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
`;

const StatUnit = styled.span`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 18px;
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textMuted};
  letter-spacing: -0.015em;
`;

const StatLabel = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  margin-top: ${theme.spacing[1]};
`;

// =============================================================================
// FIRST-RUN NAVY HERO
// =============================================================================

const FirstRunHero = styled.div`
  position: relative;
  padding: ${theme.spacing[10]} ${theme.spacing[8]};
  border-radius: ${theme.borderRadius.xl};
  background:
    radial-gradient(120% 80% at 100% 0%, rgba(255, 255, 255, 0.10) 0%, transparent 60%),
    linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[700]} 100%);
  color: rgba(255, 255, 255, 0.95);
  overflow: hidden;
  isolation: isolate;
  border: 1px solid ${theme.colors.primary[700]};
  box-shadow: ${theme.shadows.md};
  margin-bottom: ${theme.spacing[8]};
  text-align: left;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.045 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    opacity: 0.7;
    pointer-events: none;
    mix-blend-mode: screen;
    z-index: -1;
  }

  &::after {
    content: '';
    position: absolute;
    top: -120px;
    right: -120px;
    width: 360px;
    height: 360px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.18) 0%, transparent 65%);
    pointer-events: none;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[8]} ${theme.spacing[5]};
  }
`;

const FirstRunEyebrow = styled.div`
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: ${theme.spacing[3]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${theme.colors.success[500]};
    box-shadow: 0 0 0 3px ${theme.colors.success[500]}33;
  }
`;

const FirstRunTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 32px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: #fff;
  margin: 0 0 ${theme.spacing[2]};
  letter-spacing: -0.025em;
  line-height: 1.15;
  max-width: 640px;

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: 26px;
  }
`;

const FirstRunSub = styled.p`
  color: rgba(255, 255, 255, 0.75);
  font-size: ${theme.typography.fontSize.base};
  margin: 0 0 ${theme.spacing[6]};
  max-width: 580px;
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const FirstRunChipRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[6]};
  max-width: 820px;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const FirstRunChip = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: ${theme.borderRadius.lg};
  color: #fff;
  font-family: inherit;
  text-align: left;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);

  & .icon {
    width: 38px;
    height: 38px;
    border-radius: ${theme.borderRadius.md};
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.22);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  & .body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  & .label {
    color: #fff;
    font-weight: ${theme.typography.fontWeight.semibold};
    font-size: ${theme.typography.fontSize.sm};
    letter-spacing: -0.005em;
  }

  & .meta {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.65);
    font-weight: ${theme.typography.fontWeight.semibold};
    letter-spacing: 0.18em;
    text-transform: uppercase;
  }

  & .desc {
    margin-top: 2px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
  }
`;

const FirstRunFooter = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
  padding-top: ${theme.spacing[5]};
  border-top: 1px solid rgba(255, 255, 255, 0.15);
`;

const FirstRunCTA = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: #fff;
  color: ${theme.colors.primary[700]};
  border: 1px solid #fff;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base};
  box-shadow: 0 6px 14px -6px rgba(0, 0, 0, 0.4);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 18px -6px rgba(0, 0, 0, 0.5);
  }
`;

const FirstRunGhost = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & code {
    font-family: ${theme.typography.fontFamily.mono};
    font-size: 11px;
    color: rgba(255, 255, 255, 0.85);
    background: rgba(255, 255, 255, 0.10);
    padding: 2px 6px;
    border-radius: ${theme.borderRadius.sm};
    border: 1px solid rgba(255, 255, 255, 0.14);
  }
`;

// =============================================================================
// LIBRARY SECTION
// =============================================================================

const LibrarySection = styled.section`
  margin-bottom: ${theme.spacing[8]};
  animation: ${viewSwap} 0.3s ease-out both;
`;

const SectionHead = styled.div`
  margin-bottom: ${theme.spacing[5]};
`;

const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${theme.colors.primary[500]};
  margin-bottom: ${theme.spacing[2]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & .num {
    color: ${theme.colors.textMuted};
    font-variant-numeric: tabular-nums;
  }
`;

const SubHeading = styled.h2`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[1]};
  letter-spacing: -0.015em;
`;

const SubHelper = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0;
  max-width: 540px;
`;

// Sticky toolbar holding search + chips + view toggle.
const StickyToolbar = styled.div`
  position: sticky;
  top: 0;
  z-index: 8;
  margin: 0 -${theme.spacing[4]} ${theme.spacing[5]};
  padding: ${theme.spacing[4]};
  background: rgba(248, 250, 251, 0.85);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  border-bottom: 1px solid ${theme.colors.borderLight};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 240px;
  max-width: 480px;
`;

const SearchIconLeft = styled.span`
  position: absolute;
  left: ${theme.spacing[3]};
  display: inline-flex;
  align-items: center;
  color: ${theme.colors.textMuted};
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]} ${theme.spacing[3]} ${theme.spacing[10]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSize.sm};
  font-family: inherit;
  transition: all ${theme.transitions.base};

  &:hover:not(:focus) {
    border-color: ${theme.colors.borderHover};
    background: ${theme.colors.surfaceHover};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    background: ${theme.colors.surface};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}1a, 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const FilterChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
`;

type ChipTone = 'primary' | 'muted';

const FilterChip = styled.button<{ $active: boolean; $tone: ChipTone; $soon?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 7px ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: all ${theme.transitions.base};

  ${({ $active, $tone }) =>
    $active && $tone === 'primary'
      ? css`
          background: ${theme.colors.primary[500]};
          color: #fff;
          border: 1px solid ${theme.colors.primary[600]};
          box-shadow: 0 4px 10px -4px rgba(0, 0, 41, 0.35);

          &:hover {
            background: ${theme.colors.primary[700]};
            transform: translateY(-1px);
          }
        `
      : css`
          background: ${theme.colors.surface};
          color: ${theme.colors.textSecondary};
          border: 1px solid ${theme.colors.borderLight};

          &:hover {
            background: ${theme.colors.surfaceHover};
            border-color: ${theme.colors.borderHover};
            color: ${theme.colors.textPrimary};
          }
        `}

  /* "Coming soon" treatment: subtly muted, dashed border bottom-border hint. */
  ${({ $soon }) =>
    $soon &&
    css`
      border-style: dashed !important;

      &::after {
        content: 'soon';
        margin-left: 4px;
        font-size: 9px;
        font-weight: ${theme.typography.fontWeight.bold};
        letter-spacing: 0.18em;
        text-transform: uppercase;
        opacity: 0.55;
      }
    `}
`;

const ToolbarRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-left: auto;
`;

const SearchMatchInfo = styled.div`
  width: 100%;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-variant-numeric: tabular-nums;

  & code {
    font-family: ${theme.typography.fontFamily.mono};
    color: ${theme.colors.textPrimary};
    background: ${theme.colors.backgroundTertiary};
    padding: 2px 6px;
    border-radius: ${theme.borderRadius.sm};
    font-size: 11px;
  }
`;

// =============================================================================
// LIST + GRID
// =============================================================================

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const ListView = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};

  & > * {
    animation: ${fadeUp} 0.4s ease-out both;
  }

  /* Subtle stagger on the first ten rows. */
  & > *:nth-child(1) { animation-delay: 0ms; }
  & > *:nth-child(2) { animation-delay: 30ms; }
  & > *:nth-child(3) { animation-delay: 60ms; }
  & > *:nth-child(4) { animation-delay: 90ms; }
  & > *:nth-child(5) { animation-delay: 120ms; }
  & > *:nth-child(6) { animation-delay: 150ms; }
  & > *:nth-child(7) { animation-delay: 180ms; }
  & > *:nth-child(8) { animation-delay: 210ms; }
  & > *:nth-child(9) { animation-delay: 240ms; }
  & > *:nth-child(10) { animation-delay: 270ms; }

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }
`;

const GridView = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const GridCard = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[5]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    border-color ${theme.transitions.base};
  overflow: hidden;
  animation: ${fadeUp} 0.4s ease-out both;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${theme.colors.primary[500]} 0%,
      ${theme.colors.primary[700]} 100%
    );
    opacity: 0;
    transition: opacity ${theme.transitions.base};
  }

  &:hover {
    transform: translateY(-2px);
    border-color: ${theme.colors.primary[200]};
    box-shadow: 0 12px 24px -12px rgba(0, 0, 41, 0.22);
  }

  &:hover::before {
    opacity: 1;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}33;
  }

  &:hover .grid-chev {
    transform: translateX(4px);
    color: ${theme.colors.primary[500]};
  }
`;

const GridCardTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
`;

const GridFileTile = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.primary[50]};
  color: ${theme.colors.primary[500]};
  border: 1px solid ${theme.colors.primary[100]};
  flex-shrink: 0;
`;

type ScoreTone = 'success' | 'primary' | 'warning' | 'muted';

const scoreToneFor = (s: number | null | undefined): ScoreTone => {
  if (s == null) return 'muted';
  if (s >= 80) return 'success';
  if (s >= 50) return 'primary';
  if (s >= 1) return 'warning';
  return 'muted';
};

const ScorePill = styled.div<{ $tone: ScoreTone }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 12px;
  font-weight: ${theme.typography.fontWeight.semibold};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  border: 1px solid;
  flex-shrink: 0;

  & .label {
    font-family: ${theme.typography.fontFamily.primary};
    font-size: 9px;
    font-weight: ${theme.typography.fontWeight.semibold};
    letter-spacing: 0.18em;
    text-transform: uppercase;
    opacity: 0.7;
  }

  ${({ $tone }) =>
    $tone === 'success'
      ? css`
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[700]};
          border-color: ${theme.colors.success[500]}40;
        `
      : $tone === 'primary'
      ? css`
          background: ${theme.colors.primary[50]};
          color: ${theme.colors.primary[700]};
          border-color: ${theme.colors.primary[200]};
        `
      : $tone === 'warning'
      ? css`
          background: ${theme.colors.warning[100]};
          color: ${theme.colors.warning[700]};
          border-color: ${theme.colors.warning[500]}40;
        `
      : css`
          background: ${theme.colors.backgroundTertiary};
          color: ${theme.colors.textMuted};
          border-color: ${theme.colors.borderLight};
        `}
`;

const GridTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  letter-spacing: -0.012em;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const GridMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const MetaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px ${theme.spacing[2]};
  background: ${theme.colors.backgroundTertiary};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.full};
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textSecondary};
  font-variant-numeric: tabular-nums;

  & .icon {
    color: ${theme.colors.textMuted};
    display: inline-flex;
    align-items: center;
  }
`;

const GridSummary = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: calc(${theme.typography.fontSize.sm} * 1.625 * 3);
`;

const GridFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: ${theme.spacing[3]};
  border-top: 1px dashed ${theme.colors.borderLight};
`;

const UsageChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textMuted};
  font-variant-numeric: tabular-nums;

  & .icon {
    display: inline-flex;
    align-items: center;
  }
`;

const GridChev = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${theme.colors.textMuted};
  transition: transform ${theme.transitions.base}, color ${theme.transitions.base};
`;

// =============================================================================
// LOADING / ERROR / EMPTY
// =============================================================================

const LoadingShell = styled.div`
  padding: ${theme.spacing[16]} ${theme.spacing[6]};
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

const LoadingSpinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid ${theme.colors.borderLight};
  border-top-color: ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const ErrorBand = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[5]};
  background: ${theme.colors.error[100]};
  border: 1px solid ${theme.colors.error[500]}33;
  border-left: 3px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing[6]};

  & .icon {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: ${theme.borderRadius.md};
    background: ${theme.colors.surface};
    color: ${theme.colors.error[600]};
    border: 1px solid ${theme.colors.error[500]}40;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  & .body {
    flex: 1;
    min-width: 0;
  }

  & h4 {
    font-family: ${theme.typography.fontFamily.display};
    font-size: ${theme.typography.fontSize.base};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.error[700]};
    margin: 0 0 ${theme.spacing[1]};
    letter-spacing: -0.01em;
  }

  & p {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.error[700]};
    line-height: ${theme.typography.lineHeight.relaxed};
    margin: 0 0 ${theme.spacing[3]};
  }
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${theme.colors.error[600]};
  color: #fff;
  border: 1px solid ${theme.colors.error[700]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: background ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.error[700]};
  }
`;

const SearchEmptyRow = styled.div`
  padding: ${theme.spacing[10]} ${theme.spacing[6]};
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  background: ${theme.colors.surface};
  border: 1px dashed ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[3]};

  & .icon {
    width: 40px;
    height: 40px;
    border-radius: ${theme.borderRadius.full};
    background: ${theme.colors.backgroundTertiary};
    color: ${theme.colors.textMuted};
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  & h4 {
    font-family: ${theme.typography.fontFamily.display};
    font-size: ${theme.typography.fontSize.base};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textPrimary};
    margin: 0;
    letter-spacing: -0.01em;
  }

  & code {
    font-family: ${theme.typography.fontFamily.mono};
    color: ${theme.colors.textPrimary};
    background: ${theme.colors.backgroundTertiary};
    padding: 2px 6px;
    border-radius: ${theme.borderRadius.sm};
    font-size: 11px;
  }
`;

// =============================================================================
// HELPERS
// =============================================================================

// Transform API data to match component expectations
const transformTenderDocument = (doc: TenderDocumentData) => ({
  id: doc.id,
  title: doc.title || 'Untitled Document',
  company: doc.company_id, // Using company_id as company for now
  industry: doc.industry_sector || 'Unknown',
  date: new Date(doc.created_at).toISOString().split('T')[0], // Format as YYYY-MM-DD
  // Preserve null/undefined so the row can render "Not scored" instead
  // of misleadingly showing 0% (which looks like a failed evaluation).
  score: doc.score ?? null,
  usageCount: 0, // This would need to be tracked separately in the backend
  summary: doc.summary || doc.description || 'No summary available',
  content: 'Full tender document content...', // This would be the actual content from the document
  patterns: Math.floor(Math.random() * 20), // Placeholder - would need actual pattern extraction
  writingStyle: {
    tone: 'Professional',
    formality: 'Medium',
    technicalLanguage: true,
  },
});

// =============================================================================
// COMPONENT
// =============================================================================

const TenderBankPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [tenderDocuments, setTenderDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<{
    total_documents: number;
    overall_avg_score: number;
    by_resource_type: Array<{
      resource_type: string;
      count: number;
      avg_score: number;
    }>;
  }>({
    total_documents: 0,
    overall_avg_score: 0,
    by_resource_type: [],
  });

  // Extract the load function so it can be called from handleUploadSuccess
  const loadTenderDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Load documents with search and filter parameters
      const documentsResponse = await TenderDocumentApi.getTenderDocuments(
        1, // page
        100, // page_size - load all for now
        undefined, // resourceTypeFilter
        industryFilter !== 'all' ? industryFilter : undefined, // industryFilter
        searchTerm || undefined // search
      );

      if (documentsResponse.success && documentsResponse.data) {
        const transformedDocs = documentsResponse.data.documents.map(transformTenderDocument);
        setTenderDocuments(transformedDocs);
      } else {
        setError(documentsResponse.error || 'Failed to load tender documents');
        setTenderDocuments([]);
      }

      // Load stats
      const statsResponse = await TenderDocumentApi.getTenderDocumentStats();
      if (statsResponse.success && statsResponse.data) {
        setStats(statsResponse.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while loading data');
      setTenderDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, industryFilter]);

  // Load tender documents from API
  useEffect(() => {
    loadTenderDocuments();
  }, [loadTenderDocuments]); // Re-load when search or filter changes

  const filteredDocuments = tenderDocuments.filter(doc => {
    const matchesSearch =
      doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.summary.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesIndustry =
      industryFilter === 'all' || doc.industry.toLowerCase() === industryFilter.toLowerCase();
    return matchesSearch && matchesIndustry;
  });

  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    switch (sortBy) {
      case 'score':
        // null scores sort last regardless of direction — comparing
        // `null - n` would give NaN and produce unstable ordering.
        return (b.score ?? -1) - (a.score ?? -1);
      case 'usage':
        return b.usageCount - a.usageCount;
      case 'title':
        return a.title.localeCompare(b.title);
      case 'date':
      default:
        return new Date(b.date).getTime() - new Date(a.date).getTime();
    }
  });

  const handleTenderClick = (tenderId: string) => {
    navigate(`/tender-bank/${tenderId}`);
  };

  const handleAddTender = () => {
    setShowUploadModal(true);
  };

  const handleUploadSuccess = () => {
    // Reload data after successful upload
    setShowUploadModal(false);
    // Force re-fetch by calling the load function directly
    loadTenderDocuments();
  };

  const handleDeleteTender = useCallback(async (tenderId: string) => {
    // Soft-delete on the server (status="deleted") + cascade Chroma chunks
    // — see tender_documents_routes.py::delete_tender_document_endpoint.
    // Optimistic confirm via native dialog; if the backend rejects, the
    // reload will re-add the row and the user sees the truth.
    const ok = window.confirm(
      'Delete this tender from the bank? This removes it from retrieval; the row is preserved server-side for audit.'
    );
    if (!ok) return;
    try {
      const resp = await TenderDocumentApi.deleteTenderDocument(tenderId);
      if (!resp.success) {
        window.alert(`Delete failed: ${resp.error || 'unknown error'}`);
        return;
      }
      await loadTenderDocuments();
    } catch (err) {
      window.alert(`Delete failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [loadTenderDocuments]);

  const totalDocuments = stats.total_documents || tenderDocuments.length;
  const averageScore = Math.round(stats.overall_avg_score || 0);
  const totalUsage = tenderDocuments.reduce((sum, doc) => sum + doc.usageCount, 0);
  const industries = Array.from(new Set(tenderDocuments.map(doc => doc.industry)));

  // Adaptive header status — success-tone when populated, primary-tone when empty.
  const headerStatus = useMemo<{ tone: StatusTone; label: React.ReactNode }>(() => {
    if (totalDocuments === 0) {
      return {
        tone: 'primary',
        label: <>Empty library</>,
      };
    }
    return {
      tone: 'success',
      label: (
        <>
          <strong>{totalDocuments.toLocaleString()}</strong>{' '}
          {totalDocuments === 1 ? 'tender archived' : 'tenders archived'}
        </>
      ),
    };
  }, [totalDocuments]);

  // Whether we're in active search/filter mode
  const isSearching = searchTerm.trim().length > 0 || industryFilter !== 'all';

  return (
    <TenderBankContainer>
      <Particles
        particleCount={25}
        particleColor={theme.colors.primary[300]}
        speed={0.2}
        opacity={0.3}
      />
      <ContentWrapper>
        <Container>
          {/* ========================= PAGE HEADER ========================= */}
          <PageHeader>
            <HeaderLeft>
              <Crumb>Tender bank</Crumb>
              <Title>Tender bank</Title>
              <Subtitle>
                Your archive of past tenders. The AI mines this library for patterns, voice, and
                evidence when drafting new responses.
              </Subtitle>
            </HeaderLeft>
            <HeaderRight>
              <StatusPill $tone={headerStatus.tone}>
                <span className="pulse" />
                <span>{headerStatus.label}</span>
              </StatusPill>
              <ActionsRow>
                <PrimaryButton type="button" onClick={handleAddTender}>
                  <Plus size={16} />
                  Add to bank
                </PrimaryButton>
              </ActionsRow>
            </HeaderRight>
          </PageHeader>

          {/* ========================= STATS GRID ========================= */}
          {!loading && !error && totalDocuments > 0 && (
            <StatsGrid>
              <StatCard>
                <StatIconTile $tone="primary">
                  <Database size={16} />
                </StatIconTile>
                <StatBody>
                  <StatValueWrap>
                    <StatValue>{totalDocuments.toLocaleString()}</StatValue>
                  </StatValueWrap>
                  <StatLabel>Total documents</StatLabel>
                </StatBody>
              </StatCard>
              <StatCard>
                <StatIconTile $tone="success">
                  <BarChart3 size={16} />
                </StatIconTile>
                <StatBody>
                  <StatValueWrap>
                    <StatValue>{averageScore}</StatValue>
                    <StatUnit>%</StatUnit>
                  </StatValueWrap>
                  <StatLabel>Average score</StatLabel>
                </StatBody>
              </StatCard>
              <StatCard>
                <StatIconTile $tone="warning">
                  <TrendingUp size={16} />
                </StatIconTile>
                <StatBody>
                  <StatValueWrap>
                    <StatValue>{totalUsage.toLocaleString()}</StatValue>
                  </StatValueWrap>
                  <StatLabel>Total uses</StatLabel>
                </StatBody>
              </StatCard>
              <StatCard>
                <StatIconTile $tone="accent">
                  <Building2 size={16} />
                </StatIconTile>
                <StatBody>
                  <StatValueWrap>
                    <StatValue>{industries.length.toLocaleString()}</StatValue>
                  </StatValueWrap>
                  <StatLabel>Industries</StatLabel>
                </StatBody>
              </StatCard>
            </StatsGrid>
          )}

          {/* ========================= MAIN CONTENT ========================= */}
          <ContentArea>
            {loading ? (
              <LoadingShell>
                <LoadingSpinner />
                Loading your tender bank…
              </LoadingShell>
            ) : error ? (
              <ErrorBand role="alert">
                <span className="icon">
                  <AlertCircle size={16} />
                </span>
                <div className="body">
                  <h4>We couldn't load your tender bank</h4>
                  <p>{error}</p>
                  <RetryButton type="button" onClick={() => window.location.reload()}>
                    <Sparkles size={14} />
                    Try again
                  </RetryButton>
                </div>
              </ErrorBand>
            ) : totalDocuments === 0 ? (
              /* First-run hero — the empty library moment. */
              <FirstRunHero>
                <FirstRunEyebrow>
                  <span className="dot" />
                  Getting started
                </FirstRunEyebrow>
                <FirstRunTitle>Build your tender library.</FirstRunTitle>
                <FirstRunSub>
                  Drop in past bids — wins, losses, and the in-between. The AI mines them for
                  patterns and replicates your voice on every new draft.
                </FirstRunSub>
                <FirstRunChipRow>
                  <FirstRunChip>
                    <span className="icon">
                      <Archive size={18} />
                    </span>
                    <span className="body">
                      <span className="meta">Step 01</span>
                      <span className="label">Archive past bids</span>
                      <span className="desc">PDF or DOCX — recent submissions work best.</span>
                    </span>
                  </FirstRunChip>
                  <FirstRunChip>
                    <span className="icon">
                      <Sparkles size={18} />
                    </span>
                    <span className="body">
                      <span className="meta">Step 02</span>
                      <span className="label">Capture your voice</span>
                      <span className="desc">Tone, formality, and technical depth — learned.</span>
                    </span>
                  </FirstRunChip>
                  <FirstRunChip>
                    <span className="icon">
                      <Award size={18} />
                    </span>
                    <span className="body">
                      <span className="meta">Step 03</span>
                      <span className="label">Boost win rates</span>
                      <span className="desc">Patterns from winning bids steer new drafts.</span>
                    </span>
                  </FirstRunChip>
                </FirstRunChipRow>
                <FirstRunFooter>
                  <FirstRunCTA type="button" onClick={handleAddTender}>
                    <Plus size={16} />
                    Add your first tender
                  </FirstRunCTA>
                  <FirstRunGhost>
                    Accepted: <code>PDF</code> <code>DOCX</code> <code>TXT</code>
                  </FirstRunGhost>
                </FirstRunFooter>
              </FirstRunHero>
            ) : (
              <LibrarySection>
                <SectionHead>
                  <Eyebrow>
                    <span className="num">01</span>
                    <span>·</span>
                    <span>Library</span>
                  </Eyebrow>
                  <SubHeading>Browse your tenders</SubHeading>
                  <SubHelper>
                    Search by title, company, or summary content. Click any tender to inspect its
                    patterns, scores, and writing style.
                  </SubHelper>
                </SectionHead>

                <StickyToolbar>
                  <SearchInputWrapper>
                    <SearchIconLeft>
                      <Search size={16} />
                    </SearchIconLeft>
                    <SearchInput
                      type="text"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      placeholder="Search tenders by title, company, or summary…"
                      aria-label="Search tenders"
                    />
                  </SearchInputWrapper>
                  <FilterChipsRow>
                    <FilterChip
                      type="button"
                      $active={industryFilter !== 'all'}
                      $tone="primary"
                      $soon
                      title="Industry filtering — coming soon"
                      onClick={() => {
                        /* Implement filter dropdown */
                      }}
                    >
                      <Building2 size={12} />
                      {industryFilter === 'all' ? 'All industries' : industryFilter}
                      <ChevronDown size={11} />
                    </FilterChip>
                    <FilterChip
                      type="button"
                      $active={false}
                      $tone="muted"
                      $soon
                      title="Sort options — coming soon"
                      onClick={() => {
                        /* Implement sort dropdown */
                      }}
                    >
                      <SortAsc size={12} />
                      Sort: {sortBy.charAt(0).toUpperCase() + sortBy.slice(1)}
                      <ChevronDown size={11} />
                    </FilterChip>
                  </FilterChipsRow>
                  <ToolbarRight>
                    <ViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
                  </ToolbarRight>
                  {isSearching && searchTerm.trim().length > 0 && (
                    <SearchMatchInfo>
                      {sortedDocuments.length}{' '}
                      {sortedDocuments.length === 1 ? 'match' : 'matches'} for{' '}
                      <code>{searchTerm.trim()}</code>
                    </SearchMatchInfo>
                  )}
                </StickyToolbar>

                {sortedDocuments.length === 0 ? (
                  <SearchEmptyRow>
                    <span className="icon">
                      <Search size={20} />
                    </span>
                    <h4>No tenders match your filters</h4>
                    <p>
                      {searchTerm.trim() ? (
                        <>
                          Nothing found for <code>{searchTerm.trim()}</code>. Try a broader query or
                          clear the filter.
                        </>
                      ) : (
                        <>
                          Nothing matches the current filter. Try resetting to{' '}
                          <code>All industries</code>.
                        </>
                      )}
                    </p>
                  </SearchEmptyRow>
                ) : viewMode === 'list' ? (
                  <ListView>
                    {sortedDocuments.map(document => (
                      <TenderRow
                        key={document.id}
                        document={document}
                        onClick={() => handleTenderClick(document.id)}
                        onDelete={handleDeleteTender}
                      />
                    ))}
                  </ListView>
                ) : (
                  <GridView>
                    {sortedDocuments.map(document => {
                      const tone = scoreToneFor(document.score);
                      return (
                        <GridCard
                          key={document.id}
                          type="button"
                          onClick={() => handleTenderClick(document.id)}
                          aria-label={`Open ${document.title}`}
                        >
                          <GridCardTopRow>
                            <GridFileTile>
                              <FileText size={18} />
                            </GridFileTile>
                            <ScorePill $tone={tone}>
                              <span className="label">Score</span>
                              {document.score != null && document.score > 0 ? document.score : '—'}
                            </ScorePill>
                          </GridCardTopRow>
                          <GridTitle>{document.title}</GridTitle>
                          <GridMeta>
                            <MetaPill>
                              <span className="icon">
                                <Calendar size={11} />
                              </span>
                              {new Date(document.date).toLocaleDateString(undefined, {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })}
                            </MetaPill>
                            {document.industry && document.industry !== 'Unknown' && (
                              <MetaPill>
                                <span className="icon">
                                  <Building2 size={11} />
                                </span>
                                {document.industry}
                              </MetaPill>
                            )}
                          </GridMeta>
                          <GridSummary>{document.summary}</GridSummary>
                          <GridFooter>
                            <UsageChip>
                              <span className="icon">
                                <Users size={12} />
                              </span>
                              {document.usageCount.toLocaleString()}{' '}
                              {document.usageCount === 1 ? 'use' : 'uses'}
                            </UsageChip>
                            <GridChev className="grid-chev">
                              <ChevronRight size={16} />
                            </GridChev>
                          </GridFooter>
                        </GridCard>
                      );
                    })}
                  </GridView>
                )}
              </LibrarySection>
            )}
          </ContentArea>
        </Container>
      </ContentWrapper>

      {showUploadModal && (
        <TenderUploadModal
          onClose={() => setShowUploadModal(false)}
          onUpload={async documents => {
            console.log('Uploading documents:', documents);
            handleUploadSuccess();
            // The useEffect will automatically reload the data
          }}
        />
      )}

      <TenderBankAssistant />
    </TenderBankContainer>
  );
};

export default TenderBankPage;
